import { Alert, Chip } from '@mui/material';
import {
  BooleanField, BooleanInput, Create, Datagrid, DateField, Edit, FunctionField, List, SimpleForm, TextField, TextInput, TopToolbar, required, useRecordContext
} from 'react-admin';
import { BackLink } from './back';
import { RichTextInput } from 'ra-input-rich-text';
import { SeoFields } from './seoFields';

/**
 * Editable pages and blocks.
 *
 * A page has its own address on the web (/osobne-udaje); a block is a section
 * inside another page (the opening hours on the contact page). They are edited
 * the same way, so they share a screen and are told apart by a label.
 *
 * The body is HTML. It reaches the storefront exactly as written, which is the
 * same trust the Twig template it replaces carried - anyone who can edit these
 * pages could already change the templates.
 */

interface ContentRecord {
  id: number;
  code: string;
  kind: 'page' | 'block';
  title: string;
  body: string;
  isVisible: boolean;
}

const KIND_LABEL: Record<string, string> = { page: 'Stránka', block: 'Blok' };

/** Pages that the storefront routes by code, so the address can be shown. */
const PAGE_URLS: Record<string, string> = {
  'osobne-udaje': '/osobne-udaje'
};

function KindChip() {
  const record = useRecordContext<ContentRecord>();
  if (record == null) return null;

  return (
    <Chip
      size="small"
      variant="outlined"
      label={KIND_LABEL[record.kind] ?? record.kind}
      color={record.kind === 'page' ? 'primary' : 'default'}
    />
  );
}

export function ContentList() {
  return (
    <List title="Obsah stránok" perPage={50} sort={{ field: 'title', order: 'ASC' }} exporter={false}>
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="title" label="Názov" />
        <FunctionField label="Typ" render={() => <KindChip />} />
        <FunctionField
          label="Kód"
          render={(record: ContentRecord) => PAGE_URLS[record.code] ?? record.code}
        />
        <BooleanField source="isVisible" label="Zobrazené" />
        <DateField source="updated" label="Upravené" showTime />
      </Datagrid>
    </List>
  );
}

function CodeNotice() {
  const record = useRecordContext<ContentRecord>();
  if (record == null) return null;

  return (
    <Alert severity="info" variant="outlined" sx={{ mb: 2, width: '100%' }}>
      {record.kind === 'page' ? (
        <>
          Adresa <strong>{PAGE_URLS[record.code] ?? record.code}</strong> sa nedá zmeniť — odkazujú na
          ňu existujúce linky aj vyhľadávače.
        </>
      ) : (
        <>
          Blok <strong>{record.code}</strong> zobrazuje šablóna webu na presnom mieste. Kód sa preto
          nedá zmeniť; ak blok nechcete zobraziť, vypnite „Zobrazené“.
        </>
      )}
    </Alert>
  );
}

/** Blocks live inside another page, so they have no address to optimise. */
function PageSeo() {
  const record = useRecordContext<ContentRecord>();
  if (record == null || record.kind !== 'page') return null;

  return <SeoFields uri={PAGE_URLS[record.code]?.replace(/^\//, '') ?? record.code} />;
}

export function ContentEdit() {
  return (
    <Edit title="Obsah" mutationMode="pessimistic" redirect="list" actions={<TopToolbar sx={{ justifyContent: 'flex-start' }}><BackLink /></TopToolbar>}>
      <SimpleForm>
        <CodeNotice />
        <TextInput source="title" label="Názov" validate={required()} fullWidth />
        <BooleanInput source="isVisible" label="Zobrazené na webe" />
        <RichTextInput source="body" label="Obsah" fullWidth />
        <PageSeo />
      </SimpleForm>
    </Edit>
  );
}

export function ContentCreate() {
  return (
    <Create title="Nová stránka" redirect="list">
      <SimpleForm defaultValues={{ kind: 'page', isVisible: true, body: '' }}>
        <Alert severity="info" variant="outlined" sx={{ mb: 2, width: '100%' }}>
          Nová stránka je dostupná až potom, čo sa jej kód doplní do webu. Na úpravu existujúcich
          stránok to netreba.
        </Alert>
        <TextInput source="title" label="Názov" validate={required()} fullWidth />
        <TextInput
          source="code"
          label="Kód"
          validate={required()}
          helperText="Malé písmená, číslice a pomlčky, napríklad reklamacny-poriadok."
          fullWidth
        />
        <BooleanInput source="isVisible" label="Zobrazené na webe" />
        <RichTextInput source="body" label="Obsah" fullWidth />
      </SimpleForm>
    </Create>
  );
}
